<template>
  <div class="flex min-h-screen flex-col bg-gray-50 px-4 py-8">
    <div class="mx-auto w-full max-w-sm">
      <!-- Branding -->
      <div class="mb-4 flex items-center justify-center gap-2">
        <img src="/assets/trellis-logo.svg" alt="Trellis" class="h-6 w-auto" />
        <span class="text-xs font-medium text-gray-400">ID Verification</span>
      </div>

      <!-- Step indicator -->
      <div class="mb-4 flex justify-center gap-2">
        <div class="h-2 w-6 rounded-full bg-green-600" />
        <div class="h-2 w-2 rounded-full bg-gray-300" />
      </div>

      <!-- === UPLOADING STATE === -->
      <template v-if="uploading">
        <div class="mt-12 text-center">
          <h1 class="text-lg font-semibold text-gray-900">Uploading…</h1>

          <div class="mt-6 flex flex-col items-center gap-3">
            <div class="h-2 w-full overflow-hidden rounded-full bg-gray-200">
              <div
                class="h-full rounded-full bg-green-600 transition-all duration-300"
                :style="{ width: `${uploadProgress}%` }"
              />
            </div>
            <p class="text-sm text-gray-500">{{ uploadProgress }}%</p>
          </div>

          <div v-if="uploadError" class="mt-4">
            <p class="text-sm text-red-600">{{ uploadError }}</p>
            <button
              type="button"
              class="mt-2 rounded-lg bg-green-600 px-4 py-2 text-sm text-white"
              @click="submitPhoto"
            >
              Retry
            </button>
          </div>
        </div>
      </template>

      <!-- === INSTRUCTIONS + CAPTURE STATE === -->
      <template v-else>
        <div class="mt-8 text-center">
          <div
            class="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-green-50"
          >
            <svg
              class="h-10 w-10 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm1.294 6.336a6.721 6.721 0 01-3.17.789 6.721 6.721 0 01-3.168-.789 3.376 3.376 0 016.338 0z"
              />
            </svg>
          </div>

          <h1 class="text-xl font-semibold text-gray-900">Photo of Your ID</h1>

          <p class="mt-3 text-sm leading-relaxed text-gray-500">
            We need a clear photo of your government-issued ID. Your driver's
            license or state ID works best.
          </p>

          <div class="mt-6 space-y-2 text-left">
            <div class="flex items-start gap-3 rounded-lg bg-white p-3">
              <span class="mt-0.5 text-green-600">✓</span>
              <span class="text-sm text-gray-600"
                >Place your ID on a flat, well-lit surface</span
              >
            </div>
            <div class="flex items-start gap-3 rounded-lg bg-white p-3">
              <span class="mt-0.5 text-green-600">✓</span>
              <span class="text-sm text-gray-600"
                >Make sure all text and the photo are clearly visible</span
              >
            </div>
            <div class="flex items-start gap-3 rounded-lg bg-white p-3">
              <span class="mt-0.5 text-green-600">✓</span>
              <span class="text-sm text-gray-600"
                >Avoid glare or shadows on the card</span
              >
            </div>
          </div>

          <!-- Preview of captured photo -->
          <div v-if="previewUrl" class="mt-6">
            <img
              :src="previewUrl"
              alt="Captured ID"
              class="mx-auto rounded-xl"
            />
          </div>

          <input
            ref="fileInput"
            type="file"
            accept="image/*"
            capture="environment"
            class="hidden"
            @change="onFileSelected"
          />

          <button
            type="button"
            class="mt-8 w-full rounded-xl bg-green-600 px-6 py-3.5 text-sm font-semibold text-white shadow-sm transition-all active:scale-[0.98]"
            @click="openCamera"
          >
            {{ previewUrl ? "Retake Photo" : "Take Photo" }}
          </button>

          <button
            v-if="capturedFile"
            type="button"
            class="mt-3 w-full rounded-xl bg-green-700 px-6 py-3.5 text-sm font-semibold text-white shadow-sm transition-all active:scale-[0.98]"
            @click="submitPhoto"
          >
            Use This Photo
          </button>
        </div>
      </template>

      <!-- Participant name -->
      <p class="mt-4 text-center text-xs text-gray-400">
        {{ participantName }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onUnmounted, ref } from "vue"

const props = defineProps<{
  token: string
  participantName: string
  status: string
}>()

const fileInput = ref<HTMLInputElement | null>(null)
const capturedFile = ref<File | null>(null)
const previewUrl = ref<string | null>(null)
const uploading = ref(false)
const uploadProgress = ref(0)
const uploadError = ref<string | null>(null)

function openCamera() {
  fileInput.value?.click()
}

function onFileSelected(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return

  capturedFile.value = file
  input.value = ""

  // User already confirmed in camera — submit immediately
  submitPhoto()
}

async function submitPhoto() {
  if (!capturedFile.value) return

  uploading.value = true
  uploadProgress.value = 0
  uploadError.value = null

  try {
    const formData = new FormData()
    formData.append("photo", capturedFile.value)

    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest()

      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) {
          uploadProgress.value = Math.round((e.loaded / e.total) * 100)
        }
      })

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 400) {
          resolve()
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`))
        }
      })

      xhr.addEventListener("error", () => {
        reject(new Error("Network error during upload"))
      })

      xhr.open("POST", `/verification/${props.token}/document`)

      const csrfToken = document.querySelector<HTMLMetaElement>(
        'meta[name="csrf-token"]',
      )?.content
      if (csrfToken) {
        xhr.setRequestHeader("X-CSRF-Token", csrfToken)
      }

      xhr.send(formData)
    })

    window.location.href = `/verification/${props.token}/face`
  } catch (err) {
    uploading.value = false
    uploadError.value =
      err instanceof Error ? err.message : "Upload failed. Please try again."
  }
}

onUnmounted(() => {
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value)
  }
})

defineOptions({ layout: false })
</script>
